import {useEffect, useRef} from 'react';
import {View, StyleSheet, Animated, Easing, ScrollView} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';

export const DashboardSkeleton = () => {
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme);

    // Shimmer animation
    const shimmerAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const shimmer = Animated.loop(
            Animated.sequence([
                Animated.timing(shimmerAnim, {
                    toValue: 1,
                    duration: 1500,
                    easing: Easing.linear,
                    useNativeDriver: true,
                }),
                Animated.timing(shimmerAnim, {
                    toValue: 0,
                    duration: 1500,
                    easing: Easing.linear,
                    useNativeDriver: true,
                }),
            ])
        );
        shimmer.start();
        return () => shimmer.stop();
    }, [shimmerAnim]);

    const shimmerOpacity = shimmerAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0.2, 1.0],
    });

    const SkeletonBox = ({style}: {style?: any}) => (
        <Animated.View style={[styles.skeleton, {opacity: shimmerOpacity}, style]} />
    );

    const HEADER_HEIGHT = 200 + insets.top;

    return (
        <View style={styles.container}>
            {/* Header Skeleton */}
            <View style={[styles.header, {height: HEADER_HEIGHT, paddingTop: insets.top}]}>
                <View style={styles.headerContent}>
                    {/* Organization Icon */}
                    <SkeletonBox style={styles.orgIcon} />

                    {/* Organization Info */}
                    <View style={styles.orgInfo}>
                        <SkeletonBox style={styles.orgName} />
                        <SkeletonBox style={styles.orgSubtitle} />
                    </View>
                </View>
            </View>

            <ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{paddingTop: HEADER_HEIGHT}}
            >
                {/* Featured Videos Skeleton */}
                <View style={styles.videoSection}>
                    <View style={styles.sectionHeader}>
                        <SkeletonBox style={styles.sectionTitle} />
                        <SkeletonBox style={styles.sectionAction} />
                    </View>
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={styles.videoList}
                    >
                        {[1, 2, 3, 4].map((i) => (
                            <View key={i} style={styles.videoCard}>
                                <SkeletonBox style={styles.videoThumbnail} />
                                <View style={styles.videoInfo}>
                                    <SkeletonBox style={styles.videoTitle} />
                                    <SkeletonBox style={styles.videoMeta} />
                                </View>
                            </View>
                        ))}
                    </ScrollView>
                </View>

                {/* Last Viewed Videos Skeleton */}
                <View style={styles.videoSection}>
                    <View style={styles.sectionHeader}>
                        <SkeletonBox style={styles.sectionTitle} />
                        <SkeletonBox style={styles.sectionAction} />
                    </View>
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={styles.videoList}
                    >
                        {[1, 2, 3].map((i) => (
                            <View key={i} style={styles.videoCard}>
                                <SkeletonBox style={styles.videoThumbnail} />
                                <View style={styles.videoInfo}>
                                    <SkeletonBox style={styles.videoTitle} />
                                    <SkeletonBox style={styles.videoMeta} />
                                </View>
                            </View>
                        ))}
                    </ScrollView>
                </View>

                {/* Achievements Skeleton */}
                <View style={styles.achievementSection}>
                    <View style={styles.sectionHeader}>
                        <SkeletonBox style={styles.sectionTitle} />
                        <SkeletonBox style={styles.sectionAction} />
                    </View>
                    {[1, 2, 3].map((i) => (
                        <View key={i} style={styles.achievementCard}>
                            <SkeletonBox style={styles.achievementIcon} />
                            <View style={styles.achievementInfo}>
                                <SkeletonBox style={styles.achievementTitle} />
                                <SkeletonBox style={styles.achievementDesc} />
                            </View>
                        </View>
                    ))}
                </View>

                {/* Quick Actions Grid Skeleton */}
                <View style={styles.quickActionsSection}>
                    <View style={styles.quickActionsGrid}>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <View key={i} style={styles.quickActionTile}>
                                <SkeletonBox style={styles.quickActionIcon} />
                                <SkeletonBox style={styles.quickActionTitle} />
                                <SkeletonBox style={styles.quickActionSubtitle} />
                            </View>
                        ))}
                    </View>
                </View>

                {/* OpenWebApp Button Skeleton */}
                <View style={styles.webAppSection}>
                    <SkeletonBox style={styles.webAppButton} />
                </View>

                <View style={styles.bottomSpacing} />
            </ScrollView>
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollView: {
        flex: 1,
    },
    skeleton: {
        backgroundColor: theme.colors.border,
        borderRadius: theme.borderRadius.sm,
    },

    // Header
    header: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: theme.colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        zIndex: 1000,
    },
    headerContent: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.lg,
    },
    orgIcon: {
        width: 80,
        height: 80,
        borderRadius: theme.borderRadius.lg,
        marginRight: theme.spacing.md,
    },
    orgInfo: {
        flex: 1,
    },
    orgName: {
        height: 24,
        width: '70%',
        marginBottom: theme.spacing.xs,
    },
    orgSubtitle: {
        height: 16,
        width: '50%',
    },

    // Quick Actions
    quickActionsSection: {
        padding: theme.spacing.md,
    },
    quickActionsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing.sm,
    },
    quickActionTile: {
        width: '48%',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    quickActionIcon: {
        width: 40,
        height: 40,
        borderRadius: theme.borderRadius.md,
        marginBottom: theme.spacing.sm,
    },
    quickActionTitle: {
        height: 16,
        width: '80%',
        marginBottom: theme.spacing.xs,
    },
    quickActionSubtitle: {
        height: 12,
        width: '60%',
    },

    // Video Sections
    videoSection: {
        marginTop: theme.spacing.md,
        paddingHorizontal: theme.spacing.md,
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.md,
    },
    sectionTitle: {
        height: 20,
        width: 150,
    },
    sectionAction: {
        height: 16,
        width: 80,
    },
    videoList: {
        marginHorizontal: -theme.spacing.md,
        paddingHorizontal: theme.spacing.md,
    },
    videoCard: {
        width: 200,
        marginRight: theme.spacing.md,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    videoThumbnail: {
        width: '100%',
        height: 112,
        borderTopLeftRadius: theme.borderRadius.lg,
        borderTopRightRadius: theme.borderRadius.lg,
    },
    videoInfo: {
        padding: theme.spacing.sm,
    },
    videoTitle: {
        height: 16,
        width: '90%',
        marginBottom: theme.spacing.xs,
    },
    videoMeta: {
        height: 12,
        width: '60%',
    },

    // Achievements
    achievementSection: {
        marginTop: theme.spacing.xl,
        paddingHorizontal: theme.spacing.md,
    },
    achievementCard: {
        flexDirection: 'row',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    achievementIcon: {
        width: 60,
        height: 60,
        borderRadius: theme.borderRadius.md,
        marginRight: theme.spacing.md,
    },
    achievementInfo: {
        flex: 1,
        justifyContent: 'center',
    },
    achievementTitle: {
        height: 16,
        width: '70%',
        marginBottom: theme.spacing.xs,
    },
    achievementDesc: {
        height: 12,
        width: '90%',
    },

    // WebApp Button
    webAppSection: {
        marginTop: theme.spacing.xl,
        paddingHorizontal: theme.spacing.md,
    },
    webAppButton: {
        height: 80,
        width: '100%',
        borderRadius: theme.borderRadius.lg,
    },

    bottomSpacing: {
        height: theme.spacing.xl,
    },
});
